import React, { useState, useRef } from 'react';
import CustomModal from '../../../Global/CustomModal';
import { useSnackbar } from 'notistack';
import {
  updateSchoolImageBE,
  uploadSchoolPictureToS3,
} from '../../../../services/schools';

interface SchoolPictureUploaderProps {
  schoolId: string;
  isDialogOpen: boolean;
  setIsDialogOpen: (isDialogOpen: boolean) => void;
  loadSchools: () => void;
}

const SchoolPictureUploader: React.FC<SchoolPictureUploaderProps> = ({
  schoolId,
  isDialogOpen,
  setIsDialogOpen,
  loadSchools,
}) => {
  const [image, setImage] = useState<string>();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { enqueueSnackbar } = useSnackbar();

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];

      // Check file type
      const allowedTypes = [
        'image/png',
        'image/jpeg',
        'image/jpg',
        'image/heic',
      ];
      if (!allowedTypes.includes(file.type.toLowerCase())) {
        enqueueSnackbar('Please upload only PNG, JPG, JPEG, or HEIC files', {
          autoHideDuration: 3000,
        });
        return;
      }

      // Check file size (5MB = 5 * 1024 * 1024 bytes)
      const maxSize = 5 * 1024 * 1024;
      if (file.size > maxSize) {
        enqueueSnackbar('File size must be less than 5MB', {
          autoHideDuration: 3000,
        });
        return;
      }

      const reader = new FileReader();
      reader.addEventListener('load', () => {
        if (typeof reader.result === 'string') {
          setImage(reader.result);
          setIsDialogOpen(true);
        }
      });
      reader.readAsDataURL(file);
    }
  };

  const handleCropImage = async () => {
    if (image) {
      try {
        // Convert base64 to blob directly
        const response = await fetch(image);
        const blob = await response.blob();
        const uniqueFilename = schoolId;
        const croppedFile = new File([blob], uniqueFilename, {
          type: 'image/jpeg',
        });
        const avatarUrl = await uploadSchoolPictureToS3(croppedFile);
        updateSchoolImage(avatarUrl);
        setIsDialogOpen(false);
      } catch (error) {
        console.error('Error processing image:', error);
      }
    }
  };

  const updateSchoolImage = async (avatarUrl: string) => {
    try {
      const updatedSchool = await updateSchoolImageBE(schoolId, avatarUrl);
      if (updatedSchool) {
        enqueueSnackbar('School image updated successfully', {
          autoHideDuration: 3000,
        });
        loadSchools();
      }
    } catch (error) {
      console.error('Error updating school image:', error);
      enqueueSnackbar('Failed to update school image', {
        autoHideDuration: 3000,
      });
    }
  };

  return (
    <CustomModal
      open={isDialogOpen}
      onClose={() => setIsDialogOpen(false)}
      name={'school-picture-uploader'}
      glass={true}
      blurs={true}
    >
      <div className="modal_header">
        <h2>Choose School Picture</h2>
      </div>
      <div className="modal_content">
        <input
          type="file"
          accept=".png,.jpg,.jpeg,.heic"
          onChange={handleFileChange}
          ref={fileInputRef}
        />
      </div>
      <div className="modal_actions">
        <button
          className="button button--glass"
          onClick={() => setIsDialogOpen(false)}
        >
          Cancel
        </button>
        <button
          className="button button--glass"
          onClick={handleCropImage}
          disabled={!image}
        >
          Save
        </button>
      </div>
    </CustomModal>
  );
};

export default SchoolPictureUploader;
